import { config } from '~/config';
import {
  getBrandedPdfContent,
  PDF_WATERMARK_PRINT_CSS,
} from '~/utils/pdf-branding/pdf-branding';

import type { ConversationInventory } from '../services/inventory-service';
import { ConversationTask } from '../use-chat/use-chat.types';
import { VisualizationTable } from '../use-visualization-store/useVisualizationStore';
import { buildInventoryContent } from './buildInventoryContent';
import { BODY_STYLE, htmlNodes } from './htmlNodes';
import { markdownParser } from './markdownParser';

export type CopyAction = 'copy' | 'print';

interface CopyResearchMessageArgs {
  task: ConversationTask;
  charts: Record<string, HTMLDivElement>;
  tables: Record<string, VisualizationTable>;
  inventory?: ConversationInventory;
  action: CopyAction;
  taskQuestion: string;
}

export const copyResearchMessage = async ({
  task,
  charts,
  tables,
  inventory,
  action,
  taskQuestion,
}: CopyResearchMessageArgs) => {
  if (task.states) {
    let { htmlContent, plainContent } = task.states.reduce(
      (result, curr) => {
        result.plainContent += `\n\n## ${curr.title}\n\n`;
        result.htmlContent += htmlNodes.h2(curr.title);

        const processedHtmlContent = markdownParser({
          content: curr.content,
          charts,
          tables,
        });

        result.plainContent += curr.content;
        result.htmlContent += processedHtmlContent + '\n';
        return result;
      },
      {
        htmlContent: '',
        plainContent: '',
      }
    );

    const { html: inventoryHtml, text: inventoryText } =
      buildInventoryContent({ inventory });

    plainContent += inventoryText;
    htmlContent += inventoryHtml;

    if (action === 'print') {
      htmlContent = getBrandedPdfContent(config.urls.root, htmlContent);
    }

    const printDocument = `
  <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${taskQuestion}</title>
        <style>
          body { ${BODY_STYLE} }
          @media print {
            ${PDF_WATERMARK_PRINT_CSS}
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
  `;

    if (action === 'print') {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(iframe);
          }, 100);
        };
      }
    } else {
      const clipboardData: Record<string, Blob> = {
        'text/plain': new Blob([plainContent.trim()], { type: 'text/plain' }),
        'text/html': new Blob([printDocument], { type: 'text/html' }),
      };

      await navigator.clipboard.write([new ClipboardItem(clipboardData)]);
    }
  }
};
